import { Link, useNavigate } from "react-router-dom";
import { Heart, Truck } from "lucide-react";
import { formatINR } from "@/lib/lamka";
import { useAuth } from "@/context/AuthContext";
import { shareLinks, WhatsAppIcon } from "@/components/ShareButtons";

const splitSizes = (s) => (s || "").split(/[,/|]/).map((x) => x.trim()).filter(Boolean);
const deliveryLabel = (t) => {
  const v = (t || "").trim();
  if (!v || /^same\s*day$/i.test(v)) return "Same Day Delivery";
  return /deliver/i.test(v) ? v : `Delivery in ${v}`;
};

export const ThriftInfo = ({ item, onToggleLike }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const sizes = splitSizes(item.size);
  const soldOut = item.stock === 0;

  const buyNow = () => {
    if (!user) return navigate("/login");
    if (sizes.length > 1) return navigate(`/product/${item.id}`);
    const q = new URLSearchParams({ qty: "1", ...(sizes[0] && { size: sizes[0] }) });
    navigate(`/checkout/${item.id}?${q}`);
  };

  return (
    <div data-testid={`thrift-info-${item.id}`} className="pt-4 text-center space-y-1">
      <Link to={`/product/${item.id}`} data-testid={`thrift-item-link-${item.id}`} draggable={false}>
        <h3 data-testid={`thrift-item-title-${item.id}`} className="font-display text-[15px] leading-snug text-[#0F0F0F] line-clamp-2 hover:text-[#8C6D33] transition-colors">{item.title}</h3>
      </Link>
      <p data-testid={`thrift-item-price-${item.id}`} className="font-mono text-xs text-[#8C6D33]">{formatINR(item.price)}</p>
      {item.size && <p data-testid={`thrift-item-size-${item.id}`} className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#0F0F0F]/55">Size · {item.size}</p>}
      <p data-testid={`thrift-delivery-label-${item.id}`} className="inline-flex items-center gap-1 font-mono text-[8.5px] uppercase tracking-[0.16em] text-[#8C6D33]">
        <Truck className="h-3 w-3" strokeWidth={1.75} /> {deliveryLabel(item.delivery_time)}
      </p>
      <div className="flex items-center justify-center gap-1.5 pt-2">
        <button data-testid={`thrift-buy-${item.id}`} onClick={buyNow} disabled={soldOut} className="flex-1 min-w-0 h-10 rounded-full bg-[#C5A059] text-[#0F0F0F] font-mono text-[10px] uppercase tracking-[0.14em] transition-[background-color,transform] duration-200 hover:bg-[#D4B06A] active:scale-95 disabled:opacity-50">
          {soldOut ? "Sold out" : "Buy Now"}
        </button>
        <button data-testid={`thrift-like-${item.id}`} onClick={() => onToggleLike(item)} aria-label={item.is_liked ? "Remove from wishlist" : "Save to wishlist"} aria-pressed={!!item.is_liked} className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center border border-[#0F0F0F]/20 transition-[transform,border-color] duration-200 hover:border-[#C5A059] active:scale-90">
          <Heart className={`h-4 w-4 ${item.is_liked ? "fill-[#C5A059] text-[#C5A059]" : "text-[#0F0F0F]"}`} strokeWidth={1.5} />
        </button>
        <a data-testid={`thrift-share-whatsapp-${item.id}`} href={shareLinks(item).wa} target="_blank" rel="noopener noreferrer" draggable={false} aria-label="Share on WhatsApp" className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center bg-[#25D366] text-[#0F0F0F] transition-[transform,filter] duration-200 hover:brightness-95 active:scale-90">
          <WhatsAppIcon />
        </a>
      </div>
    </div>
  );
};
